import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConfigClientService } from '../../api';
import { PasswordPolicyDto } from '../../api/model/passwordPolicyDto';

/** Fallback when the API is unavailable (e.g. network error, unauthenticated). Matches previous hardcoded rules. */
const DEFAULT_POLICY: PasswordPolicyDto = {
  minLength: 12,
  maxLength: 128,
  requireLowercase: true,
  requireUppercase: true,
  requireDigit: true,
  requireSpecialCharacter: true
};

@Injectable({
  providedIn: 'root'
})
export class PasswordPolicyService {
  private readonly _configClient = inject(ConfigClientService);

  private _cachedPolicy: PasswordPolicyDto | null = null;
  private _loadPromise: Promise<PasswordPolicyDto> | null = null;

  /**
   * Returns the current password policy, fetching from API once and then caching.
   * On API failure (network, 401, etc.) returns a default policy so validation and UI always work.
   */
  public getPolicy(): Promise<PasswordPolicyDto> {
    if (this._cachedPolicy !== null) {
      return Promise.resolve(this._cachedPolicy);
    }
    if (this._loadPromise !== null) {
      return this._loadPromise;
    }
    this._loadPromise = firstValueFrom(this._configClient.getPasswordPolicy())
      .then((policy) => {
        this._cachedPolicy = policy;
        return policy;
      })
      .catch(() => {
        this._cachedPolicy = DEFAULT_POLICY;
        return DEFAULT_POLICY;
      });
    return this._loadPromise;
  }

  /**
   * Returns the cached policy synchronously, or null if not yet loaded.
   * Use in validators/UI that run after ensurePolicyLoaded() has been called.
   */
  public getCachedPolicy(): PasswordPolicyDto | null {
    return this._cachedPolicy;
  }

  /**
   * Ensures the policy is loaded; resolves when the policy is available.
   * Idempotent and safe to call from multiple places.
   */
  public async ensurePolicyLoaded(): Promise<PasswordPolicyDto> {
    return this.getPolicy();
  }

  /**
   * Clears the cache (e.g. on logout) so the next use fetches fresh policy.
   */
  public clearCache(): void {
    this._cachedPolicy = null;
    this._loadPromise = null;
  }
}
